/**
 * Government Credentials API Client
 *
 * Implements GovCredentialsApiClient using tRPC to communicate with the kernel backend.
 * Uses gov-credentials.* procedures.
 */

import type {
  CredentialListItem,
  CredentialType,
  GovCredentialsApiClient,
  GovSystemListItem,
} from '@xhubio-saas/core-frontend/plugin-gov-credentials'
import { readGovCredentialSecret } from '@xhubio-saas/core-frontend/plugin-gov-credentials'
import { useMemo, useRef } from 'react'
import { trpc } from '@/trpc'

export function useGovCredentialsClient(): GovCredentialsApiClient {
  const utils = trpc.useUtils()
  const storeMutation = trpc.govCredentials.store.useMutation()
  const storeEscrowCredentialMutation = trpc.govCredentials.storeEscrowCredential.useMutation()
  // Eine Absage, ein Sprecher (Plan API 09-26/09 S3c): das gov-credentials-Plugin meldet Kontext und
  // Grund selbst (core-frontend 5.31.4, `deleteFailed` + `mapErrorToMessage`) — der Kernel-Toast schweigt.
  const deleteMutation = trpc.govCredentials.delete.useMutation({
    meta: { suppressGlobalErrorToast: true },
  })
  const testConnectionMutation = trpc.govCredentials.testConnection.useMutation()
  const setOrgPasswordMutation = trpc.govCredentials.setOrgPassword.useMutation()

  // Die Mutationen wandern in eine Ref: ihr Inhalt ist pro Render neu, die
  // Ref-Identitaet bleibt stabil. Gelesen wird `.current` NUR im asynchronen
  // Callback, nie waehrend des Renderns.
  const mutations = useRef({
    storeMutation,
    storeEscrowCredentialMutation,
    deleteMutation,
    testConnectionMutation,
    setOrgPasswordMutation,
  })
  mutations.current = {
    storeMutation,
    storeEscrowCredentialMutation,
    deleteMutation,
    testConnectionMutation,
    setOrgPasswordMutation,
  }

  return useMemo(
    () => ({
      async list(): Promise<CredentialListItem[]> {
        const result = await utils.govCredentials.list.fetch()
        return (result ?? []) as CredentialListItem[]
      },

      async listSystems(): Promise<GovSystemListItem[]> {
        const result = await utils.govCredentials.listSystems.fetch()
        return (result ?? []) as GovSystemListItem[]
      },

      // 🔴 Kein Cast: `readGovCredentialSecret` ENTSCHEIDET, ob Schluesselmaterial
      // vorliegt (core-frontend 5.0.0 gegen core-backend 1.84.0). Ohne
      // `gov-credentials:credential:create` liefert der Server dieselben
      // Metadaten, aber `ciphertext`/`iv`/`salt` als `null` plus den Vermerk
      // `gov-cred-004`. Der fruehere Cast auf den entfallenen Typ behauptete
      // daraus ein `string`: die Maske zeigte ein LEERES Geheimnis, als waere
      // es das Geheimnis. Die entschiedene Union hat im Zurueckhalte-Fall gar
      // kein `ciphertext` — es gibt keinen leeren Wert mehr, den ein Aufrufer
      // versehentlich als Geheimnis weiterreichen koennte.
      async get(countryCode: string, systemKey?: string) {
        const result = await utils.govCredentials.get.fetch({ countryCode, systemKey })
        return readGovCredentialSecret(result)
      },

      async store(params: {
        countryCode: string
        systemKey?: string
        credentialType: CredentialType
        label?: string
        encryptedPayload: { ciphertext: string; iv: string; salt: string }
        metadata?: { expiresAt?: string; taxId?: string; softwareId?: string }
      }): Promise<{ success: boolean }> {
        const result = await mutations.current.storeMutation.mutateAsync(params)
        await utils.govCredentials.list.invalidate()
        await utils.govCredentials.listSystems.invalidate()
        return result as { success: boolean }
      },

      async storeEscrowCredential(params: {
        countryCode: string
        systemKey?: string
        credentialType: CredentialType
        label?: string
        secret: Record<string, string>
        metadata?: { expiresAt?: string; taxId?: string; softwareId?: string }
      }): Promise<{ success: boolean }> {
        const result = await mutations.current.storeEscrowCredentialMutation.mutateAsync(params)
        await utils.govCredentials.list.invalidate()
        await utils.govCredentials.listSystems.invalidate()
        return result as { success: boolean }
      },

      async delete(countryCode: string, systemKey?: string): Promise<{ success: boolean }> {
        const result = await mutations.current.deleteMutation.mutateAsync({
          countryCode,
          systemKey,
        })
        await utils.govCredentials.list.invalidate()
        await utils.govCredentials.listSystems.invalidate()
        return result as { success: boolean }
      },

      async testConnection(
        countryCode: string,
        decryptedCredentials: Record<string, string>,
        systemKey?: string
      ): Promise<{ success: boolean; message: string; details?: Record<string, unknown> }> {
        const result = await mutations.current.testConnectionMutation.mutateAsync({
          countryCode,
          systemKey,
          decryptedCredentials,
        })
        await utils.govCredentials.list.invalidate()
        await utils.govCredentials.listSystems.invalidate()
        return result as { success: boolean; message: string; details?: Record<string, unknown> }
      },

      async setOrgPassword(params: { hash: string; salt: string }): Promise<{ success: boolean }> {
        const result = await mutations.current.setOrgPasswordMutation.mutateAsync(params)
        return result as { success: boolean }
      },

      async getOrgPassword(): Promise<{ hash: string; salt: string } | null> {
        const result = await utils.govCredentials.getOrgPassword.fetch()
        return (result ?? null) as { hash: string; salt: string } | null
      },
    }),
    // NUR `utils`. Keine Mutationen — sonst ist der Client pro Render neu.
    [utils]
  )
}
